const WEEKDAY_SHORT = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

export const ATTENDANCE_STATUS_LABELS = {
  presente: 'Presente',
  falta: 'Falta',
  falta_justificada: 'Falta justificada',
  outside_employment_window: 'Fora do vínculo',
  folga: 'Folga',
  troca_de_folga: 'Troca de folga',
  folga_compensatoria: 'Folga compensatória',
  folga_trabalhada: 'Folga trabalhada',
  holiday: 'Feriado',
  holiday_worked: 'Feriado trabalhado',
  sunday: 'Domingo',
};

export const ATTENDANCE_STATUS_SHORT_LABELS = {
  presente: 'PR',
  falta: 'FA',
  falta_justificada: 'FJ',
  outside_employment_window: 'FV',
  folga: 'FO',
  troca_de_folga: 'TF',
  folga_compensatoria: 'FC',
  folga_trabalhada: 'FT',
  holiday: 'FR',
  holiday_worked: 'FH',
  sunday: 'DOM',
};

export const MOZAMBIQUE_NATIONAL_HOLIDAYS = {
  '01-01': 'Ano Novo',
  '02-03': 'Dia dos Heróis Moçambicanos',
  '04-07': 'Dia da Mulher Moçambicana',
  '05-01': 'Dia Internacional dos Trabalhadores',
  '06-25': 'Dia da Independência Nacional',
  '09-07': 'Dia da Vitória',
  '09-25': 'Dia das Forças Armadas',
  '10-04': 'Dia da Paz e Reconciliação',
  '12-25': 'Dia da Família',
};

export const WORKED_ATTENDANCE_STATUSES = ['presente', 'troca_de_folga', 'folga_trabalhada'];

export const formatAttendanceStatusLabel = (status) => ATTENDANCE_STATUS_LABELS[status] || 'Sem estado';

export const isWorkedAttendanceStatus = (status) => WORKED_ATTENDANCE_STATUSES.includes(status);

export const createEmptyAttendanceSegment = (defaults = {}) => ({
  store_location: defaults.store_location || '',
  work_start: defaults.work_start || '',
  lunch_start: defaults.lunch_start || '',
  lunch_end: defaults.lunch_end || '',
  work_end: defaults.work_end || '',
});

export const calculateSegmentHours = (segment = {}) => {
  const parseTime = (value) => {
    if (!value) return null;
    const [hours, minutes] = String(value).split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    return (hours * 60) + minutes;
  };

  const start = parseTime(segment.work_start);
  const end = parseTime(segment.work_end);
  if (start === null || end === null || end <= start) return 0;

  let totalMinutes = end - start;
  const lunchStart = parseTime(segment.lunch_start);
  const lunchEnd = parseTime(segment.lunch_end);
  if (lunchStart !== null && lunchEnd !== null && lunchEnd > lunchStart) {
    totalMinutes -= Math.max(0, lunchEnd - lunchStart);
  }
  return Math.max(0, Math.round((totalMinutes / 60) * 100) / 100);
};

export const buildSegmentsSummary = (segments = []) => segments
  .filter((segment) => segment?.store_location || segment?.work_start || segment?.work_end)
  .map((segment, index) => `S${index + 1}: ${segment.store_location || 'Sem loja'} · ${segment.work_start || '—'}-${segment.work_end || '—'} · ${calculateSegmentHours(segment)}h`)
  .join(' | ');

export const buildDaysInMonth = (month, year) => {
  const totalDays = new Date(year, month, 0).getDate();
  return Array.from({ length: totalDays }, (_, index) => {
    const day = index + 1;
    const date = new Date(year, month - 1, day);
    const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const holidayName = MOZAMBIQUE_NATIONAL_HOLIDAYS[`${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`] || null;
    const isSunday = date.getDay() === 0;
    return {
      day,
      dateKey,
      weekday: WEEKDAY_SHORT[(date.getDay() + 6) % 7],
      isHoliday: Boolean(holidayName),
      holidayName,
      isSunday,
      calendarLabel: holidayName || (isSunday ? 'Domingo' : 'Dia normal'),
    };
  });
};

export const getDefaultDateForPeriod = (month, year) => {
  const now = new Date();
  const isCurrentPeriod = now.getMonth() + 1 === Number(month) && now.getFullYear() === Number(year);
  const day = isCurrentPeriod ? now.getDate() : 1;
  const safeDay = Math.min(day, new Date(year, month, 0).getDate());
  return `${year}-${String(month).padStart(2, '0')}-${String(safeDay).padStart(2, '0')}`;
};

export const normalizeAttendanceText = (value) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

export const iterateDateKeys = (startDate, endDate) => {
  if (!startDate) return [];
  const keys = [];
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${(endDate || startDate)}T00:00:00`);
  const cursor = new Date(start);
  while (cursor <= end) {
    keys.push(cursor.toISOString().slice(0, 10));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
};

export const issueTouchesDate = (issue, dateKey) => {
  const issueData = issue?.issue_data || {};
  if (issueData.date === dateKey) return true;
  if (Array.isArray(issueData.gap_dates) && issueData.gap_dates.includes(dateKey)) return true;
  return false;
};

export const buildAllocationDateMap = (allocations = []) => {
  const map = new Map();
  allocations.forEach((allocation) => {
    iterateDateKeys(allocation.start_date, allocation.end_date).forEach((dateKey) => {
      map.set(`${allocation.employee_number}::${dateKey}`, allocation);
    });
  });
  return map;
};

export const buildIssueDateMap = (issues = []) => {
  const map = new Map();
  issues.forEach((issue) => {
    const employeeKey = issue.employee_number || issue.employee_id || 'unknown';
    const explicitDates = [];
    if (issue?.issue_data?.date) explicitDates.push(issue.issue_data.date);
    if (Array.isArray(issue?.issue_data?.gap_dates)) {
      explicitDates.push(...issue.issue_data.gap_dates);
    }

    if (explicitDates.length === 0) {
      const rowKey = `${employeeKey}::row`;
      const existing = map.get(rowKey) || [];
      map.set(rowKey, [...existing, issue]);
      return;
    }

    explicitDates.forEach((dateKey) => {
      const mapKey = `${employeeKey}::${dateKey}`;
      const existing = map.get(mapKey) || [];
      map.set(mapKey, [...existing, issue]);
    });
  });
  return map;
};

export const getCellIssues = ({ issueMap, employeeNumber, dateKey }) => {
  const exactIssues = issueMap.get(`${employeeNumber}::${dateKey}`) || [];
  const rowIssues = issueMap.get(`${employeeNumber}::row`) || [];
  return [...exactIssues, ...rowIssues];
};

export const resolveAttendanceCellState = ({ record, absence, issues, allocation, employeeBaseStore, dayMeta }) => {
  const isTransfer = Boolean(allocation?.store_location && allocation.store_location !== employeeBaseStore);

  if (dayMeta?.status === 'outside_employment_window') {
    return {
      status: 'outside_employment_window',
      label: 'Fora do vínculo',
      shortLabel: 'FV',
      hoursLabel: dayMeta?.lockReason || 'Dia bloqueado',
      metaLabel: '',
      calendarLabel: dayMeta?.lockReason || '',
      readonly: true,
      lockReason: dayMeta?.lockReason || 'Dia fora da janela real de vínculo',
      isTransfer,
    };
  }

  if (record) {
    const status = record.status || 'presente';
    const hours = Number(record.total_worked_hours || record.worked_hours || 0);
    const segmentCount = Number(record.segment_count || record.segments?.length || 0);
    const hasBlockingIssue = issues.some((issue) => issue.severity === 'error');
    const workedOnHoliday = Boolean(dayMeta?.isHoliday) && isWorkedAttendanceStatus(status);
    return {
      status: hasBlockingIssue ? 'invalid' : (workedOnHoliday ? 'holiday_worked' : status),
      baseStatus: status,
      label: hasBlockingIssue ? `${formatAttendanceStatusLabel(status)} com conflito` : (workedOnHoliday ? 'Feriado trabalhado' : formatAttendanceStatusLabel(status)),
      shortLabel: workedOnHoliday ? 'FH' : (ATTENDANCE_STATUS_SHORT_LABELS[status] || 'RG'),
      hoursLabel: isWorkedAttendanceStatus(status) ? `${hours}h` : formatAttendanceStatusLabel(status),
      metaLabel: dayMeta?.holidayName || (segmentCount > 1 ? `${segmentCount} seg.` : (record.secondary_store_location ? '2 lojas' : '')),
      calendarLabel: dayMeta?.calendarLabel || '',
      isTransfer,
    };
  }

  if (absence?.absence_scope === 'partial_hours') {
    return {
      status: 'absent_partial',
      label: 'Falta parcial',
      shortLabel: 'PH',
      hoursLabel: absence.total_hours ? `${absence.total_hours}h` : 'PH',
      metaLabel: dayMeta?.holidayName || '',
      calendarLabel: dayMeta?.calendarLabel || '',
      isTransfer,
    };
  }

  if (absence) {
    return {
      status: 'falta_justificada',
      baseStatus: 'falta_justificada',
      label: 'Falta justificada',
      shortLabel: 'FJ',
      hoursLabel: absence.is_paid ? 'Pago' : 'Sem paga',
      metaLabel: dayMeta?.holidayName || '',
      calendarLabel: dayMeta?.calendarLabel || '',
      isTransfer,
    };
  }

  if (dayMeta?.isHoliday) {
    return {
      status: 'holiday',
      label: 'Feriado',
      shortLabel: 'FR',
      hoursLabel: dayMeta.holidayName || 'Feriado nacional',
      metaLabel: dayMeta.holidayName || '',
      calendarLabel: dayMeta.calendarLabel || '',
      isTransfer,
    };
  }

  if (dayMeta?.isSunday) {
    return {
      status: 'sunday',
      label: 'Domingo',
      shortLabel: 'DOM',
      hoursLabel: 'Domingo',
      metaLabel: '',
      calendarLabel: dayMeta.calendarLabel || '',
      isTransfer,
    };
  }

  if (issues.length > 0) {
    return {
      status: issues.some((issue) => issue.severity === 'error') ? 'invalid' : 'pending',
      label: issues.some((issue) => issue.severity === 'error') ? 'Validação rejeitada' : 'Pendência aberta',
      shortLabel: issues.some((issue) => issue.severity === 'error') ? 'ER' : 'PD',
      hoursLabel: `${issues.length} issue(s)`,
      calendarLabel: dayMeta?.calendarLabel || '',
      isTransfer,
    };
  }

  return {
    status: 'empty',
    label: allocation?.store_location ? 'Sem registo do dia' : 'Dia sem informação',
    shortLabel: '—',
    hoursLabel: allocation?.store_location ? allocation.store_location : 'Sem registo',
    calendarLabel: dayMeta?.calendarLabel || '',
    isTransfer,
  };
};

export const attendanceStatusTone = {
  presente: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
  falta: 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100',
  falta_justificada: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100',
  folga: 'border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200',
  troca_de_folga: 'border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100',
  folga_compensatoria: 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100',
  folga_trabalhada: 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100',
  holiday: 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100',
  holiday_worked: 'border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100',
  sunday: 'border-slate-300 bg-slate-50 text-slate-600 hover:bg-slate-100',
  absent_partial: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100',
  pending: 'border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100',
  invalid: 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100',
  empty: 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50',
};

export const sortEmployeesForAttendance = (employees, summaries, issuesByEmployee, sortField, direction) => {
  const rows = [...employees];
  const factor = direction === 'desc' ? -1 : 1;
  rows.sort((left, right) => {
    const leftSummary = summaries.get(left.employee_number) || {};
    const rightSummary = summaries.get(right.employee_number) || {};
    const leftIssues = issuesByEmployee.get(left.employee_number) || [];
    const rightIssues = issuesByEmployee.get(right.employee_number) || [];

    let leftValue;
    let rightValue;
    switch (sortField) {
      case 'hours':
        leftValue = Number(leftSummary.total_hours || 0);
        rightValue = Number(rightSummary.total_hours || 0);
        break;
      case 'location':
        leftValue = leftSummary.store_location || left.store_location || '';
        rightValue = rightSummary.store_location || right.store_location || '';
        break;
      case 'status':
        leftValue = leftIssues.length;
        rightValue = rightIssues.length;
        break;
      case 'date':
        leftValue = leftSummary.days || 0;
        rightValue = rightSummary.days || 0;
        break;
      case 'employee':
      default:
        leftValue = `${left.full_name || left.name || ''} ${left.employee_number || ''}`;
        rightValue = `${right.full_name || right.name || ''} ${right.employee_number || ''}`;
        break;
    }

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return factor * (leftValue - rightValue);
    }

    return factor * String(leftValue || '').localeCompare(String(rightValue || ''), 'pt');
  });
  return rows;
};

export const findApplicableShift = ({ shifts = [], employeeNumber, storeLocation, weekday }) => {
  const matches = shifts.filter((shift) => {
    const weekdayMatches = shift.weekday === null || shift.weekday === undefined || Number(shift.weekday) === Number(weekday);
    const employeeMatches = !shift.employee_number || shift.employee_number === employeeNumber;
    const storeMatches = !shift.store_location || shift.store_location === storeLocation;
    return weekdayMatches && employeeMatches && storeMatches;
  });

  return matches.sort((left, right) => {
    const leftPriority = (left.employee_number ? 2 : 0) + (left.store_location ? 1 : 0);
    const rightPriority = (right.employee_number ? 2 : 0) + (right.store_location ? 1 : 0);
    return rightPriority - leftPriority;
  })[0] || null;
};

export const formatShortDate = (value) => {
  if (!value) return '—';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}`;
};

export const formatAbsenceScopeLabel = (scope) => (scope === 'partial_hours' ? 'Falta parcial' : 'Falta dia inteiro');